import { isDocumentDefined } from "../element/isDocumentDefined";

/**
 * Options for setting a cookie
 */
export type SetCookieOptions = {
  /**
   * The domain the cookie is valid for.
   * Use ".huub.nl" for cross-subdomain sharing.
   * Omit for localhost development.
   */
  domain?: string;
  /**
   * The path the cookie is valid for.
   * Defaults to "/"
   */
  path?: string;
  /**
   * Whether the cookie should only be sent over HTTPS.
   * Should be true in production, false for localhost.
   */
  secure?: boolean;
  /**
   * SameSite attribute for the cookie.
   * "lax" is recommended for cross-site redirects.
   */
  sameSite?: "strict" | "lax" | "none";
  /**
   * Max age in seconds.
   * If not set, cookie will be a session cookie.
   */
  maxAge?: number;
  /**
   * Expiration date for the cookie.
   * Alternative to maxAge.
   */
  expires?: Date;
};

/**
 * Sets a cookie with the given name, value, and options.
 * @param name - The name of the cookie
 * @param value - The value to store (will be URL encoded)
 * @param options - Cookie options (domain, path, secure, sameSite, maxAge, expires)
 */
export function setCookie(
  name: string,
  value: string,
  options: SetCookieOptions = {},
): void {
  if (!isDocumentDefined()) {
    return;
  }

  const {
    domain,
    path = "/",
    secure,
    sameSite = "lax",
    maxAge,
    expires,
  } = options;

  let cookieString = `${name}=${encodeURIComponent(value)}`;

  if (domain) {
    cookieString += `; domain=${domain}`;
  }

  cookieString += `; path=${path}`;

  if (secure) {
    cookieString += "; secure";
  }

  cookieString += `; samesite=${sameSite}`;

  if (maxAge !== undefined) {
    cookieString += `; max-age=${maxAge}`;
  }

  if (expires) {
    cookieString += `; expires=${expires.toUTCString()}`;
  }

  // biome-ignore lint/suspicious/noDocumentCookie: cookie utility function
  document.cookie = cookieString;
}
